import { useOrganization } from '@clerk/react';
import { useMediaQuery, useTheme } from '@mui/material';
import { RiBarcodeLine } from '@remixicon/react';
import { useRouter } from '@tanstack/react-router';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useAuth, useIsCustomer, useIsMember } from 'src/auth';
import Link from 'src/components/atoms/Link';
import { AgButton, AgText } from 'src/components/ui';
import { useAiV2Enabled } from 'src/hooks/useAiV2Enabled';
import { FeatureFlagsClient, GLOBAL_APP_BAR_HEIGHT } from 'src/lib/constants';
import { buildAppHref } from 'src/lib/routing';
import AiChatButton from 'src/modules/ai/components/AiChatButton';
import { useAiInboxOnlyMode } from 'src/modules/ai-inbox/hooks/useAiInboxOnlyMode';
import { FeedbackButton, FeedbackDrawer } from 'src/modules/feedback';
import { GlobalSearchTrigger } from 'src/modules/globalSearch';
import { appLinks } from 'src/routing/appLinks';
import NotificationMenu from '../../components/containers/menus/NotificationMenu';
import { UserMenu } from '../../components/containers/menus/UserMenu';
import CreateNewButton from './CreateNewButton';
import { getEnvHeaderTheme } from './envHeaderTheme';

export { GLOBAL_APP_BAR_HEIGHT };

export default function SiteHeader() {
  const theme = useTheme();
  const router = useRouter();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const flags = useFlags();
  const aiV2 = useAiV2Enabled();
  const isMember = useIsMember();
  const isCustomer = useIsCustomer();
  const { currentUser } = useAuth();
  const { organization, isLoaded: orgLoaded } = useOrganization();
  const envTheme = getEnvHeaderTheme();
  const { locked: aiInboxOnly } = useAiInboxOnlyMode();

  // AI Inbox-only lockdown collapses the header to logo + bell + UserMenu.
  // Search, Create, Scan and AiChatButton all navigate to surfaces outside the
  // allow list — hide them. NotificationMenu is gated on `isMember` alone: the
  // bell opens a popover in place, so it strands no one on a blocked route.
  const showMemberChrome = isMember && !aiInboxOnly;

  return (
    <header
      className="flex fixed border-b shadow-none top-0 left-0 right-0 px-6 items-center my-auto gap-4 md:gap-3"
      style={{
        zIndex: theme.zIndex.drawer + 1,
        height: GLOBAL_APP_BAR_HEIGHT,
        background: envTheme.background,
        borderBottomColor: envTheme.borderColor,
      }}
    >
      <Link
        {...appLinks.landing()}
        label={<img style={{ height: 28 }} src={isSmallScreen ? '/icon.svg' : '/logo.svg'} alt="Agriful" />}
      />
      {isCustomer && orgLoaded && organization && (
        <>
          <div className="w-px h-6 bg-neutral-5" />
          {organization.imageUrl && (
            <div className="-mr-2">
              <img src={organization.imageUrl} width="auto" style={{ maxHeight: '28px' }} alt="" />
            </div>
          )}
          <AgText size="h6">{organization.name}</AgText>
        </>
      )}
      {showMemberChrome && <GlobalSearchTrigger />}
      <div className="flex-1" />

      <div className="flex items-center gap-2 h-full">
        {showMemberChrome && <CreateNewButton />}
        {showMemberChrome && flags.scanPage && (
          <AgButton variant="ghost" href={buildAppHref(router, appLinks.scan())}>
            <RiBarcodeLine />
            Go to Scanning
          </AgButton>
        )}
        {isMember && <NotificationMenu />}
        {showMemberChrome && !aiV2 && <AiChatButton />}
        {showMemberChrome && flags[FeatureFlagsClient.CustomerFeedback] === true && <FeedbackButton />}
        {!!currentUser && <UserMenu />}
      </div>
      {showMemberChrome && flags[FeatureFlagsClient.CustomerFeedback] === true && <FeedbackDrawer />}
    </header>
  );
}
